/**
 * Health profile form.
 */

import { useEffect, useState, type FormEvent } from 'react';
import {
  useHealthProfile,
  useUpsertHealthProfile,
  type HealthProfileInput,
} from '../../api/health';
import styles from './health.module.css';

export function HealthProfileScreen() {
  const profile = useHealthProfile();
  const upsert = useUpsertHealthProfile();
  const [form, setForm] = useState<HealthProfileInput & { notes?: string }>({});
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (profile.data) {
      setForm({
        dob: profile.data.dob,
        heightCm: profile.data.heightCm,
        weightKg: profile.data.weightKg,
        restingHr: profile.data.restingHr,
        vo2Estimate: profile.data.vo2Estimate,
        bodyFatPct: profile.data.bodyFatPct,
        notes: profile.data.notes,
      });
    }
  }, [profile.data]);

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    setSaved(false);
    upsert.mutate(form, {
      onSuccess: () => setSaved(true),
    });
  }

  function numChange(key: keyof HealthProfileInput) {
    return (e: React.ChangeEvent<HTMLInputElement>) => {
      const raw = e.target.value;
      setForm((f) => ({
        ...f,
        [key]: raw === '' ? null : Number(raw),
      }));
    };
  }

  return (
    <div className={styles.page}>
      <header className={styles.head}>
        <div>
          <h2 className={styles.title}>Health profile</h2>
          <div className={styles.subtitle}>
            Baseline measurements and notes. Agents read these to interpret
            everything else (training, nutrition, recovery). Update as values
            change.
          </div>
        </div>
      </header>

      <form className={styles.card} onSubmit={onSubmit}>
        <div className={styles.formGrid}>
          <label className={styles.label} htmlFor="dob">Date of birth</label>
          <input
            id="dob"
            className={styles.input}
            type="date"
            value={form.dob ?? ''}
            onChange={(e) =>
              setForm((f) => ({ ...f, dob: e.target.value || null }))
            }
          />

          <label className={styles.label} htmlFor="heightCm">Height (cm)</label>
          <input
            id="heightCm"
            className={styles.input}
            type="number"
            step="0.1"
            min="0"
            value={form.heightCm ?? ''}
            onChange={numChange('heightCm')}
          />

          <label className={styles.label} htmlFor="weightKg">Weight (kg)</label>
          <input
            id="weightKg"
            className={styles.input}
            type="number"
            step="0.01"
            min="0"
            value={form.weightKg ?? ''}
            onChange={numChange('weightKg')}
          />

          <label className={styles.label} htmlFor="restingHr">Resting HR (bpm)</label>
          <input
            id="restingHr"
            className={styles.input}
            type="number"
            step="1"
            min="0"
            value={form.restingHr ?? ''}
            onChange={numChange('restingHr')}
          />

          <label className={styles.label} htmlFor="vo2Estimate">VO₂ estimate</label>
          <input
            id="vo2Estimate"
            className={styles.input}
            type="number"
            step="0.1"
            min="0"
            value={form.vo2Estimate ?? ''}
            onChange={numChange('vo2Estimate')}
          />

          <label className={styles.label} htmlFor="bodyFatPct">Body fat (%)</label>
          <input
            id="bodyFatPct"
            className={styles.input}
            type="number"
            step="0.1"
            min="0"
            max="100"
            value={form.bodyFatPct ?? ''}
            onChange={numChange('bodyFatPct')}
          />

          <label className={styles.label} htmlFor="notes">Notes</label>
          <textarea
            id="notes"
            className={styles.textarea}
            value={form.notes ?? ''}
            onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
            placeholder="Injuries, history, anything an agent should know…"
          />
        </div>

        {upsert.isError && (
          <div className={styles.error}>Save failed. Try again.</div>
        )}

        <div className={styles.formFoot}>
          {saved && !upsert.isPending && (
            <span className={styles.muted}>Saved</span>
          )}
          <button
            type="submit"
            className="kos-btn kos-btn-primary"
            disabled={upsert.isPending}
          >
            {upsert.isPending ? 'Saving…' : profile.data ? 'Update profile' : 'Create profile'}
          </button>
        </div>
      </form>
    </div>
  );
}
